/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

/**
 *
 * IntegrationsForm
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Detail, DetailHeader, LoadingSpinnerSVG, SelectField } from 'cx-ui-components';
import { capitalizeFirstLetter } from 'serenova-js-utils/strings';
import DetailWrapper from '../../../components/DetailWrapper';
import styled from 'styled-components';

import ActivedirForm from './Types/activedir';
import DefaultForm from './Types/default';
import CalabrioForm from './Types/calabrio';
import ClientForm from './Types/client';
import EmailForm from './Types/email';
import LambdaForm from './Types/lambda';
import MonetForm from './Types/monet';
import RestForm from './Types/rest';
import SalesforceForm from './Types/salesforce';
import SerenovaVoiceForm from './Types/serenovaVoice';
import TeleoptiForm from './Types/teleopti';
import TwilioForm from './Types/twilio';
import EcgForm from './Types/ecg';
import VerintForm from './Types/verint';
import ZendeskForm from './Types/zendesk';
import MsTeamsForm from './Types/msteams';
import MsTeamsPresenceForm from './Types/msteams-presence';
import SalesforceServiceCloudForm from './Types/salesforceServiceCloud';
import CxQmForm from './Types/cxqm';
import AgentAssistForm from './Types/agent-assist';
import SocialConnectorForm from './Types/socialConnector';

const Wrapper = styled.div`
   display: flex;
   flex-direction: column;
 `;
const CenterWrapper = styled.div`
   text-align: center;
 `;
const WrappedDetailHeader = styled(DetailHeader)`
   margin-left: 35px;
 `;

export default function IntegrationsForm({
  authType,
  contactLayouts,
  change,
  ctiEnabled,
  currentEmail,
  dirty,
  fetchOAuthAccessToken,
  googleOauthClientId,
  googleOauthClientSecret,
  handleSubmit,
  incomingType,
  inherited,
  initialValues,
  integrationActive,
  integrationType,
  isMsTeamsIntegrationEnabled,
  isMsteamsUserSyncing,
  isSaving,
  isWebRtcEnabled,
  key,
  msOauthClientId,
  msOauthClientSecret,
  msOauthPreferredUsername,
  msteamsSyncUserData,
  msteamsSyncUserProgressData,
  msteamsUserSync,
  oauthCredential,
  oauthCredentialId,
  oauthCredentialResponse,
  onSyncUsers,
  readContactLayouts,
  onStartSalesforceOauth,
  onSalesforceOauthReturnSuccess,
  regions,
  rtaEnabled,
  selectedEntity,
  salesforceAuthVersion,
  salesforceLoginUrl,
  setImapHost,
  setOAuthCredentialId,
  setOAuthToken,
  setSmtpHost,
  setSmtpUser,
  sidePanelUpdatePermissions,
  userHasUpdatePermission,
  workItems,
  options,
}) {
  const formTypeProps = {
    isSaving,
    dirty,
    inherited,
    userHasUpdatePermission,
    initialValues,
    salesforceAuthVersion,
    salesforceLoginUrl,
    change,
    sidePanelUpdatePermissions,
    isMsteamsUserSyncing,
    integrationType,
    msteamsUserSync,
    integrationActive,
    onStartSalesforceOauth,
    onSalesforceOauthReturnSuccess,
  };
  return (
    (isSaving || isMsteamsUserSyncing) && integrationType === 'ms-teams' ?
      (
        <CenterWrapper>
          <LoadingSpinnerSVG size={90} color="#00BFFF"/>
          <CenterWrapper>{msteamsUserSync === 'sync' ? `User Sync is ${msteamsSyncUserProgressData}` : msteamsUserSync === 'toggle' ? `${isMsTeamsIntegrationEnabled ? 'Disable' : 'Enable'} integration is in progress` : 'Ms-Teams data submit is in progress'}</CenterWrapper>
        </CenterWrapper>
      ) :
      (
        <form onSubmit={handleSubmit} key={key}>
          <Wrapper>
            <DetailWrapper open data-automation="integrationDetailsSVG">
              <WrappedDetailHeader text="Details"/>
              {initialValues.get('id') !== undefined ? (
                <Detail
                  label="Type"
                  value={`${integrationType === 'rest' ? 'REST' : capitalizeFirstLetter(integrationType)}`}
                />
              ) : (
                <SelectField
                  name="type"
                  label="Type *"
                  disabled={isSaving || inherited || !userHasUpdatePermission || isMsteamsUserSyncing}
                  options={options}
                  data-automation="integrationsType"
                  required
                />
              )}

              <DefaultForm {...formTypeProps} />
              {integrationType === 'calabrio' && (
                <CalabrioForm {...formTypeProps} ctiEnabled={ctiEnabled} rtaEnabled={rtaEnabled}/>
              )}
            </DetailWrapper>
          </Wrapper>
          {integrationType === 'activedir' &&
        <ActivedirForm {...formTypeProps} contactLayouts={contactLayouts} readContactLayouts={readContactLayouts} /> }
          {integrationType === 'client' &&
            <ClientForm {...formTypeProps} />
          }
          {integrationType === 'cxengage-qm' && <CxQmForm {...formTypeProps} />}
          {integrationType === 'email' &&
            <EmailForm {...formTypeProps}
                       dirty={dirty}
                       currentEmail={currentEmail}
                       fetchOAuthAccessToken={fetchOAuthAccessToken}
                       googleOauthClientId={googleOauthClientId}
                       googleOauthClientSecret={googleOauthClientSecret}
                       incomingType={incomingType}
                       msOauthClientId={msOauthClientId}
                       msOauthClientSecret={msOauthClientSecret}
                       msOauthPreferredUsername={msOauthPreferredUsername}
                       oauthCredential={oauthCredential}
                       oauthCredentialId={oauthCredentialId}
                       oauthCredentialResponse={oauthCredentialResponse}
                       selectedEntity={selectedEntity}
                       setImapHost={setImapHost}
                       setSmtpHost={setSmtpHost}
                       setSmtpUser={setSmtpUser}
                       setOAuthCredentialId={setOAuthCredentialId}
                       setOAuthToken={setOAuthToken}
            />
          }
          {integrationType === 'lambda' && <LambdaForm {...formTypeProps} />}
          {integrationType === 'monet' && <MonetForm {...formTypeProps} />}
          {integrationType === 'rest' && <RestForm {...formTypeProps} authType={authType}/>}
          {integrationType === 'salesforce' && <SalesforceForm {...formTypeProps} />}
          {integrationType === 'ms-teams' &&
            <MsTeamsForm {...formTypeProps}
                         onSyncUsers={onSyncUsers}
                         isMsTeamsIntegrationEnabled={isMsTeamsIntegrationEnabled}
                         msteamsSyncUserData={msteamsSyncUserData}
                         msteamsSyncUserProgressData={msteamsSyncUserProgressData}/>}
          {integrationType === 'msteams-presence' &&
            <MsTeamsPresenceForm/>}
          {integrationType === 'salesforce-service-cloud' &&
            <SalesforceServiceCloudForm {...formTypeProps} />}
          {integrationType === 'serenovaVoice' && <SerenovaVoiceForm {...formTypeProps} />}
          {integrationType === 'teleopti' && <TeleoptiForm {...formTypeProps} />}
          {integrationType === 'twilio' && (
            <TwilioForm {...formTypeProps} regions={regions} isWebRtcEnabled={isWebRtcEnabled}/>
          )}
          {integrationType === 'ecg' && (
            <EcgForm {...formTypeProps} isWebRtcEnabled={isWebRtcEnabled}/>
          )}
          {integrationType === 'verint' && <VerintForm {...formTypeProps} />}
          {integrationType === 'zendesk' && <ZendeskForm {...formTypeProps} workItems={workItems}/>}
          {integrationType === 'agent-assist' && <AgentAssistForm {...formTypeProps} />}
          {integrationType === 'socialconnector' && 
            <SocialConnectorForm 
              {...formTypeProps}
            />}
        </form>
      )
  );
}

IntegrationsForm.propTypes = {
  authType: PropTypes.string,
  change: PropTypes.func,
  ctiEnabled: PropTypes.bool,
  currentEmail: PropTypes.string,
  dirty: PropTypes.bool,
  fetchOAuthAccessToken: PropTypes.func,
  googleOauthClientId: PropTypes.string,
  googleOauthClientSecret: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  incomingType: PropTypes.string,
  inherited: PropTypes.bool,
  initialValues: PropTypes.object,
  integrationActive: PropTypes.bool,
  integrationType: PropTypes.string,
  isMsTeamsIntegrationEnabled: PropTypes.bool,
  isMsteamsUserSyncing: PropTypes.bool,
  isSaving: PropTypes.bool,
  isWebRtcEnabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  key: PropTypes.string,
  msOauthClientId: PropTypes.string,
  msOauthClientSecret: PropTypes.string,
  msOauthPreferredUsername: PropTypes.string,
  msteamsSyncUserData: PropTypes.string,
  msteamsSyncUserProgressData: PropTypes.string,
  msteamsUserSync: PropTypes.string,
  oauthCredential: PropTypes.any,
  oauthCredentialId: PropTypes.string,
  oauthCredentialResponse: PropTypes.any,
  onSyncUsers: PropTypes.func.isRequired,
  onStartSalesforceOauth: PropTypes.func,
  onSalesforceOauthReturnSuccess: PropTypes.func,
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired,
    })
  ),
  rtaEnabled: PropTypes.bool,
  selectedEntity: PropTypes.object,
  salesforceAuthVersion: PropTypes.string,
  salesforceLoginUrl: PropTypes.string,
  setImapHost: PropTypes.func,
  setOAuthCredentialId: PropTypes.func,
  setOAuthToken: PropTypes.func,
  setSmtpHost: PropTypes.func,
  setSmtpUser: PropTypes.func,
  sidePanelUpdatePermissions: PropTypes.object,
  userHasUpdatePermission: PropTypes.bool,
  workItems: PropTypes.bool,
};

IntegrationsForm.defaultProps = {
  integrationType: 'rest',
  authType: 'noAuth',
  ctiEnabled: false,
  rtaEnabled: false,
};
